import type { Row } from "@tanstack/react-table";

import { Link, useSearch } from "@tanstack/react-router";
import { DownloadIcon, Redo2Icon, Trash2Icon } from "lucide-react";
import React from "react";
import { useHover } from "react-aria";
import { twMerge } from "tailwind-merge";

import { AccessibleIcon } from "@/components/accessible-icon";
import { ToggleButton, ToggleButtonGroup } from "@/components/button";
import { Checkbox } from "@/components/checkbox";
import { useMediaPreviewModal } from "@/components/media-preview-modal/useMediaPreviewModal";
import { FeatureGuard } from "@/features/auth/permissions/feature-guard";
import AssetFolder from "@/features/my-files/assets/asset-folder.svg?react";
import { useImagePreview } from "@/hooks/useImagePreview";
import { useIsMobileScreen } from "@/hooks/useIsMobileScreen";

import type { FileItem } from "../../types";

import { FILE_ICONS } from "../../assets/file-icons";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { useRestoreFilesMutation } from "../../hooks/useRestoreFilesMutation";
import { ActionMenu } from "../action-menu";
import { useDeleteFilesDialogState } from "../file-delete-dialog";
import { useRestoreFilesDialogState } from "../file-restored-dialog";

const IMAGE_EXTENSIONS = new Set(["jpeg", "jpg", "png", "webp"]);

function GridItemActionMenu({
  handleResetRowSelection,
  itemId,
}: {
  handleResetRowSelection: () => void;
  itemId: number;
}) {
  const { toggle: toggleRestoreFilesDialog } = useRestoreFilesDialogState();
  const { setSelectedItemsIds, toggle: toggleDeleteFilesDialog } =
    useDeleteFilesDialogState();
  const downloadFileMutation = useDownloadFile();

  const restoreFromTrashMutation = useRestoreFilesMutation({
    onSuccess: () => {
      handleResetRowSelection();
      toggleRestoreFilesDialog();
    },
  });

  const { folder } = useSearch({ strict: false });
  const isTrashFolder = folder === "trash";

  return (
    <ToggleButtonGroup className="absolute right-[17px] top-xl z-20">
      <ToggleButton
        className="size-4xl p-0"
        color="gray"
        isDisabled={downloadFileMutation.isPending}
        onPress={() => {
          if (typeof itemId === "number") {
            downloadFileMutation.mutate(itemId);
          }
        }}
        variant="secondary"
      >
        <AccessibleIcon className="size-2xl">
          <DownloadIcon />
        </AccessibleIcon>
      </ToggleButton>

      {isTrashFolder ? (
        <ToggleButton
          className="size-4xl p-0"
          color="gray"
          onPress={() => restoreFromTrashMutation.mutate([itemId])}
          variant="secondary"
        >
          <AccessibleIcon className="size-2xl">
            <AccessibleIcon>
              <Redo2Icon />
            </AccessibleIcon>
          </AccessibleIcon>
        </ToggleButton>
      ) : (
        <FeatureGuard feature="my-files.files.delete">
          <ToggleButton
            className="size-4xl p-0"
            color="destructive"
            onPress={() => {
              setSelectedItemsIds([itemId as number]);
              toggleDeleteFilesDialog();
            }}
            variant="secondary"
          >
            <AccessibleIcon className="size-2xl">
              <AccessibleIcon>
                <Trash2Icon />
              </AccessibleIcon>
            </AccessibleIcon>
          </ToggleButton>
        </FeatureGuard>
      )}
    </ToggleButtonGroup>
  );
}

function FolderItemGrid({
  handleResetSorting,
  href,
  id,
  name,
}: {
  handleResetSorting?: () => void;
  href: string;
  id: "graphic" | "unknown" | "video" | "website" | number;
  name: string;
}) {
  let searchParams = {};

  searchParams =
    href === "sourcefiles" || href === "archive" || href === "trash"
      ? {
          folder: href,
        }
      : {
          [`${href}Id`]: id,
        };

  return (
    <div
      className={twMerge([
        "flex items-center gap-lg rounded-sm bg-bg-secondary px-xl py-lg",
      ])}
    >
      <AssetFolder className="shrink-0" />
      <Link
        className="truncate"
        onClick={() => handleResetSorting?.()}
        search={(prev) => ({
          ...prev,
          ...searchParams,
        })}
        to="/my-files"
      >
        <span className="text-sm text-text-primary block truncate">{name}</span>
      </Link>
      {href !== "trash" && <ActionMenu href={href} isDirectory itemId={id} />}
    </div>
  );
}

type AssetItemProps = {
  handleResetRowSelection: () => void;
  handleResetSorting?: () => void;
  isSelected?: boolean;
  toggleSelected?: (event: unknown) => void;
};

function AssetItemGrid(props: AssetItemProps & FileItem) {
  const { id, isSelected = false, name, toggleSelected, type } = props;
  const { hoverProps, isHovered } = useHover({});
  const isMobile = useIsMobileScreen();
  const { openModal } = useMediaPreviewModal();
  const isImage =
    type === "file" && IMAGE_EXTENSIONS.has(props.extension.toLowerCase());
  const {
    hasFailed: imageFailed,
    onError: onImageError,
    src: imageSrc,
  } = useImagePreview({
    preferredVariant: "thumbnail",
    url: type === "file" ? props.url : undefined,
  });

  if (type === "folder") {
    const href = props.href;

    return (
      <FolderItemGrid
        handleResetSorting={props.handleResetSorting}
        href={href}
        id={id}
        name={name}
      />
    );
  }

  if (type === "file") {
    return (
      <div
        {...hoverProps}
        className={twMerge([
          "relative size-full gap-md rounded-sm px-md pb-lg pt-md",
          "hover:bg-bg-secondary-hover",
          isSelected && "border border-border-brand bg-bg-brand-primary",
          "cursor-pointer",
        ])}
        onClick={() => openModal(String(id))}
      >
        {(isSelected || isHovered || isMobile) && (
          <Checkbox
            className="absolute left-[17px] top-[22px] z-10"
            isSelected={isSelected}
            onChange={toggleSelected}
            size="md"
          />
        )}
        {(isHovered || isMobile) && (
          <GridItemActionMenu
            handleResetRowSelection={props.handleResetRowSelection}
            itemId={typeof id === "number" ? id : -1}
          />
        )}
        {isImage && props.url && !imageFailed ? (
          <div className="absolute inset-0 flex items-center justify-center w-[199px] h-[164px] mx-auto">
            <img
              className="max-w-full max-h-full"
              onError={onImageError}
              src={imageSrc}
            />
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            {props.extension in FILE_ICONS
              ? React.createElement(
                  FILE_ICONS[props.extension as keyof typeof FILE_ICONS],
                  { className: "shrink-0 size-[30px] md:size-[60px]" },
                )
              : React.createElement(FILE_ICONS.fallback, {
                  className: "shrink-0 size-[30px] md:size-[60px]",
                })}
          </div>
        )}
        <span className="absolute inset-x-0 bottom-lg truncate px-md text-center text-xs text-text-primary">
          {name}
        </span>
      </div>
    );
  }
}

export function GroupHeader({
  allSelected,
  groupTitle,
  onToggleSelection,
  showCheckbox,
  someSelected,
}: {
  allSelected: boolean;
  groupTitle: string;
  onToggleSelection: () => void;
  showCheckbox?: boolean;
  someSelected: boolean;
}) {
  return (
    <div className="flex items-center gap-lg px-xl py-lg">
      {showCheckbox && (
        <Checkbox
          aria-label={`Select all ${groupTitle}`}
          isIndeterminate={someSelected}
          isSelected={allSelected}
          onChange={onToggleSelection}
          size="md"
        />
      )}
      <h3 className="text-xs font-medium text-text-tertiary">{groupTitle}</h3>
    </div>
  );
}

export function FolderGrid({
  resetRowSelection,
  resetSorting,
  rows,
}: {
  resetRowSelection: () => void;
  resetSorting: () => void;
  rows: Row<FileItem>[];
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-md">
      {rows.map((row) => (
        <AssetItemGrid
          key={row.id}
          {...row.original}
          handleResetRowSelection={resetRowSelection}
          handleResetSorting={resetSorting}
          isSelected={row.getIsSelected()}
          toggleSelected={row.getToggleSelectedHandler()}
        />
      ))}
    </div>
  );
}

export function AssetGrid({
  resetRowSelection,
  rows,
}: {
  resetRowSelection: () => void;
  rows: Row<FileItem>[];
}) {
  return (
    <div className="grid auto-rows-[161px] grid-cols-[repeat(auto-fill,151px)] md:auto-rows-[minmax(117px,_210px)] md:grid-cols-[repeat(auto-fill,minmax(120px,215px))] gap-md">
      {rows.map((row) => (
        <AssetItemGrid
          key={row.id}
          {...row.original}
          handleResetRowSelection={resetRowSelection}
          isSelected={row.getIsSelected()}
          toggleSelected={row.getToggleSelectedHandler()}
        />
      ))}
    </div>
  );
}
